/**
 * Item 4c (2026-07-21) - extends DESIGN-wizard-invalidation.md §6's "soft,
 * not blocking" category consistency rule. Fires when a line's chosen
 * category shares no L1 branch with any OTHER line's category AND differs
 * (by L1) from the parsed requirement's assessed category - both
 * conditions use the SAME L1 threshold, per the ruled design ("L1
 * threshold (first taxonomy level)" governs the whole rule, not just half
 * of it). Never blocks Finalize; the §6 amber warning is the control for a
 * cross-branch choice, not the option list (see Defect B / categorySearch.js).
 * L1 is read from the category master's own l1_category field - never
 * hardcoded.
 */

export const getCategoryConsistencyWarning = ({ item, index, lineItems, categories, assessedCategoryCode }) => {
  const chosenCode = (item && item.spend_category ? String(item.spend_category) : '').trim();
  if (!chosenCode) return null;

  const byCode = new Map((categories || []).map((c) => [c.custom_code, c]));
  const chosenCat = byCode.get(chosenCode);
  const chosenL1 = (chosenCat && chosenCat.l1_category ? String(chosenCat.l1_category) : '').trim();
  if (!chosenCat || !chosenL1) return null; // unknown code, or no L1 data - nothing to compare

  // L1 branches used by every OTHER line with a known, valid category.
  const otherL1Counts = new Map();
  (lineItems || []).forEach((other, otherIndex) => {
    if (otherIndex === index) return;
    const otherCode = (other && other.spend_category ? String(other.spend_category) : '').trim();
    if (!otherCode) return;
    const otherCat = byCode.get(otherCode);
    const otherL1 = (otherCat && otherCat.l1_category ? String(otherCat.l1_category) : '').trim();
    if (otherL1) otherL1Counts.set(otherL1, (otherL1Counts.get(otherL1) || 0) + 1);
  });

  if (otherL1Counts.size === 0) return null; // nothing else to compare against yet
  if (otherL1Counts.has(chosenL1)) return null; // shares an L1 branch with at least one other line

  const assessedCat = byCode.get((assessedCategoryCode || '').trim());
  const assessedL1 = (assessedCat && assessedCat.l1_category ? String(assessedCat.l1_category) : '').trim();
  if (assessedL1 && assessedL1 === chosenL1) return null; // matches the assessed category's own L1 branch

  const maxCount = Math.max(...otherL1Counts.values());
  const dominantL1s = Array.from(otherL1Counts.entries())
    .filter(([, count]) => count === maxCount)
    .map(([l1]) => l1);

  return {
    message: `'${chosenCat.category_name}' is unrelated to this RFP's other categories (${dominantL1s.join(', ')}) - confirm it's intended.`,
  };
};
